// src/components/ui/forms/FormSelect/FormSelectShadcn.jsx
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';

// shadcn components
import { Button } from '../../button';
import { Input } from '../../input';

import { cn } from '../../../../utils/cn';

const FormSelectShadcn = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select option...',
  searchable = false,
  renderOption,
  disabled = false,
  variant = 'default', // 'default', 'emerald', 'blue'
  className = '',
  zIndex = 1000,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Color variants (same as FormInput for consistency)
  const variants = {
    default: {
      base: "bg-slate-700/50 border-slate-600/50",
      focus: "focus:border-slate-500/50 focus:ring-slate-500/20",
      hover: "hover:border-slate-500/50"
    },
    emerald: {
      base: "bg-slate-700/50 border-slate-600/50",
      focus: "focus:border-emerald-500/50 focus:ring-emerald-500/20",
      hover: "hover:border-slate-500/50"
    },
    blue: {
      base: "bg-slate-700/50 border-slate-600/50",
      focus: "focus:border-blue-500/50 focus:ring-blue-500/20",
      hover: "hover:border-slate-500/50"
    }
  };

  const variantStyles = variants[variant];

  // Filter options based on search
  const filteredOptions = searchable
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // Find selected option
  const selectedOption = options.find(option => option.value === value);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle option selection
  const handleOptionSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle dropdown toggle
  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen && searchable) {
      // Focus search input when opening
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <div ref={dropdownRef} className={cn("relative", className)} {...props}>
      {/* Trigger button */}
      <Button
        type="button"
        variant="outline"
        onClick={handleToggle}
        disabled={disabled}
        className={cn(
          // Base styling matching FormInput exactly
          "w-full px-4 py-3 rounded-xl transition-all duration-200",
          "text-slate-200 text-left justify-between h-auto",
          "focus:ring-2 focus:bg-slate-700/70",

          // Variant styling
          variantStyles.base,
          isOpen ? variantStyles.focus : variantStyles.hover
        )}
      >
        <span className={selectedOption ? "text-slate-200" : "text-slate-400"}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={cn(
            "w-4 h-4 text-slate-400 transition-transform duration-200 ml-2",
            isOpen && "rotate-180"
          )}
        />
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={cn(
            // Exact dropdown styling
            "absolute w-full mt-2 rounded-xl overflow-hidden",
            "bg-slate-800/95 backdrop-blur-xl border border-slate-700/50",
            "shadow-2xl shadow-black/50"
          )}
          style={{ zIndex }}
        >
          {/* Search input */}
          {searchable && (
            <div className="p-3 border-b border-slate-700/50">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <Input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search options..."
                  className={cn(
                    "w-full pl-10 pr-4 py-2 rounded-lg",
                    "bg-slate-700/50 border border-slate-600/50",
                    "focus:border-blue-500/50"
                  )}
                />
              </div>
            </div>
          )}

          {/* Options list */}
          <div className="max-h-64 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-slate-400 text-sm">
                No options found
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = option.value === value;

                return (
                  <button
                    key={option.value || index}
                    type="button"
                    onClick={() => handleOptionSelect(option)}
                    className={cn(
                      "w-full text-left px-4 py-3 transition-all duration-200",
                      "hover:bg-slate-700/50 flex items-center justify-between",
                      isSelected && "bg-blue-500/10 text-blue-300"
                    )}
                  >
                    <div className="flex-1">
                      {renderOption ? renderOption(option) : option.label}
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-blue-400" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FormSelectShadcn;